import React, { useState, useEffect, useMemo } from 'react';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import { BOOKMAKER_ALIASES } from '../utils/constants';
import { useBooks } from '../contexts/BooksContext';
import AddToScratchpadLink from './AddToScratchpadLink';
import ModalBrandingBar from './ModalBrandingBar';
import PlayerStatsChart from './PlayerStatsChart';
import { getPrimaryLinePoint } from '../utils/gameLogChartConfig';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const NHLPlayerModal = ({
  show,
  onHide,
  playerName,
  playerTeam,
  playerPosition,
  playerId,
  gameId,
  market,
  point,
  awayTeam,
  homeTeam,
  getTeamLogoUrl,
  getBookLogoUrl,
  onOddsClick,
  onRemoveBet,
  betslipItems = [],
  bookmakers = [],
  onToggleScratchpad,
  isInScratchpad,
  recalcEdgePlayer = false,
}) => {
  const { isBookEnabledForGraphs } = useBooks();
  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMarketType, setSelectedMarketType] = useState('goals');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tooltipData, setTooltipData] = useState(null);

  // Chart state
  const [showChart, setShowChart] = useState(false);
  const [chartMarket, setChartMarket] = useState(null);
  const [chartPoint, setChartPoint] = useState(null);
  const [chartIsOver, setChartIsOver] = useState(true);
  const [oddsHistoryData, setOddsHistoryData] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  // Stats chart state moved into the shared <PlayerStatsChart> component —
  // it owns statsData, statsYear (Current/Last toggle), showDNPs, etc.

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate implied probability from American odds
  const calculateImpliedProbability = (odds) => {
    if (!odds || odds === '-') return null;
    const numericOdds = parseInt(odds);
    if (isNaN(numericOdds)) return null;

    if (numericOdds > 0) {
      return (100 / (numericOdds + 100)) * 100;
    } else {
      return (Math.abs(numericOdds) / (Math.abs(numericOdds) + 100)) * 100;
    }
  };

  // Helper to get the actual data key for a bookmaker
  // Note: API now returns data with display names (e.g., "caesars" not "williamhill_us")
  // so we just return the book name directly
  const getBookmakerDataKey = (displayBook) => {
    return displayBook;
  };

  // Filter a line.books map down to enabled books when recalcEdgePlayer
  // is on so the edge/tier calc reflects only the sportsbooks the user
  // actually sees on this modal.
  const getScopedBooks = (bookMap) => {
    if (!bookMap) return [];
    if (recalcEdgePlayer && bookmakers.length > 0) {
      return bookmakers
        .map(bk => bookMap[getBookmakerDataKey(bk)])
        .filter(Boolean);
    }
    return Object.values(bookMap);
  };

  // Handle odds cell click — on desktop this positions a popover tooltip,
  // on mobile it opens a bottom sheet (handled in render). betMeta carries
  // the full bet shape so the sheet's Add/Remove button can round-trip
  // through onOddsClick / onRemoveBet without the caller needing to repeat
  // the add-to-betslip wiring on tap.
  const handleOddsClick = (event, odds, book, point, bestBook, bestBookDiff, lineBooks, betMeta = null) => {
    if (!odds || odds === '-') return;

    const impliedProb = calculateImpliedProbability(odds);
    if (!impliedProb) return;

    // Calculate edge using the SAME logic as getTierStyle for consistency
    let edge = 0;
    if (lineBooks) {
      const allOdds = getScopedBooks(lineBooks)
        .map(b => b.over || b.yes)
        .filter(o => o !== null && o !== undefined);

      if (allOdds.length >= 2) {
        const bestOdds = Math.max(...allOdds);
        const secondBestOdds = Math.max(...allOdds.filter(o => o !== bestOdds));
        const bestOddsCount = allOdds.filter(o => o === bestOdds).length;
        const isBest = odds === bestOdds && bestOddsCount === 1;

        if (isBest) {
          // Handle cross-zero comparison (positive best odds vs negative second best)
          let diff;
          if (bestOdds > 0 && secondBestOdds < 0) {
            const spreadPositive = bestOdds - 100;
            const spreadNegative = secondBestOdds - (-100);
            const totalSpread = spreadPositive - spreadNegative;
            diff = Math.abs(totalSpread / secondBestOdds);
          } else {
            diff = (bestOdds - secondBestOdds) / Math.abs(secondBestOdds);
          }
          edge = diff * 100;
        }
      }
    }

    let x, y;
    if (isMobile) {
      x = window.innerWidth / 2 - 100;
      y = Math.min(event.clientY, window.innerHeight - 150);
    } else {
      x = event.clientX;
      y = event.clientY;
    }

    setTooltipData({
      odds,
      book,
      point,
      impliedProb: impliedProb.toFixed(2),
      edge: edge.toFixed(2),
      x,
      y,
      meta: betMeta,
    });
  };

  // Desktop auto-dismiss on outside click. Mobile uses a bottom sheet with
  // an explicit close + Add/Remove buttons, so it intentionally does NOT
  // auto-dismiss — tapping another odds cell replaces the sheet contents.
  useEffect(() => {
    if (!isMobile && tooltipData) {
      const handleClickOutside = () => setTooltipData(null);
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [tooltipData, isMobile]);

  // Fetch odds history for a specific market and tier
  const fetchOddsHistory = async (marketKey, point = null, isOver = true) => {
    if (!gameId || !playerId || !marketKey) {
      console.error('Missing required parameters for odds history');
      return;
    }

    setLoadingChart(true);
    setError(null);
    try {
      const response = await fetch(`/api/games/nhl/${gameId}/${playerId}/${marketKey}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setOddsHistoryData(data);
      setSelectedTier(point);
      setShowChart(true);
      setChartMarket(marketKey);
      setChartPoint(point);
      setChartIsOver(isOver);
    } catch (err) {
      console.error('Error fetching odds history:', err);
      setError(err.message);
    } finally {
      setLoadingChart(false);
    }
  };

  const handleBackToTable = () => {
    setShowChart(false);
    setChartMarket(null);
    setChartPoint(null);
    setChartIsOver(true);
    setOddsHistoryData(null);
    setSelectedTier(null);
  };

  // Fetch player data when modal opens
  useEffect(() => {
    if (show && playerId && gameId) {
      // Set initial market type based on the market prop
      if (market) {
        // Map market names to category names
        const marketToCategoryMap = {
          'player_goals': 'goals',
          'player_assists': 'assists',
          'player_points': 'points',
          'player_shots_on_goal': 'shots',
          'player_total_saves': 'saves',
          'player_blocked_shots': 'blocked_shots',
          'player_power_play_points': 'power_play_points',
          'player_goal_scorer_first': 'goal_scorers',
          'player_goal_scorer_last': 'goal_scorers'
        };
        const marketCategory = marketToCategoryMap[market] || market.replace('player_', '');
        setSelectedMarketType(marketCategory);
      }
      fetchPlayerData();
    } else if (!show) {
      // Reset state when modal closes
      setPlayerData(null);
      setError(null);
      setSelectedMarketType('goals');
      setShowChart(false);
      setChartMarket(null);
      setChartPoint(null);
      setChartIsOver(true);
      setOddsHistoryData(null);
      setSelectedTier(null);
    }
  }, [show, playerId, gameId, market]);

  const fetchPlayerData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/games/nhl/${gameId}/${playerId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch player data');
      }

      const data = await response.json();

      // Transform the API data to our component format
      // NHL headshot URL format: https://assets.nhle.com/mugs/nhl/:season/:teamAbbr/:playerId.png
      const nhlPlayerId = playerId?.replace('nhl_', '');

      // Calculate season from game start time
      // NHL season spans two years: if month is Oct-Dec, it's startYear+nextYear, else prevYear+startYear
      let season = '20242025'; // Default fallback
      if (data.gameInfo?.startTime) {
        const gameDate = new Date(data.gameInfo.startTime);
        const year = gameDate.getFullYear();
        const month = gameDate.getMonth(); // 0-11

        // If October (9) through December (11), season is year + (year+1)
        // If January (0) through September (8), season is (year-1) + year
        if (month >= 9) {
          season = `${year}${year + 1}`;
        } else {
          season = `${year - 1}${year}`;
        }
      }

      const photoUrl = data.team && nhlPlayerId
        ? `https://assets.nhle.com/mugs/nhl/${season}/${data.team}/${nhlPlayerId}.png`
        : null;

      const transformedData = {
        player: {
          name: data.playerName,
          team: data.team,
          teamLogo: data.teamLogo || null, // Include teamLogo from API response if available
          position: playerPosition,
          photo: photoUrl,
          jerseyNumber: null
        },
        markets: []
      };

      // Process NHL markets
      // Goals (over/under only)
      if (data.player_goals && Array.isArray(data.player_goals)) {
        const goalsLines = processMarketLines(data.player_goals, 'player_goals');

        transformedData.markets.push({
          marketKey: 'player_goals',
          marketName: 'Goals',
          category: 'goals',
          lines: goalsLines
        });
      }

      // First/Last Goal Scorer (combined market like NFL)
      const firstLastGoalMarket = {
        marketKey: 'player_first_last_goals',
        marketName: 'Goal Scorers',
        category: 'goal_scorers',
        lines: []
      };

      // Add first goal lines
      if (data.player_goal_scorer_first && Array.isArray(data.player_goal_scorer_first)) {
        const firstGoalLines = processMarketLines(data.player_goal_scorer_first, 'player_goal_scorer_first');
        firstLastGoalMarket.lines.push(...firstGoalLines);
      }

      // Add last goal lines
      if (data.player_goal_scorer_last && Array.isArray(data.player_goal_scorer_last)) {
        const lastGoalLines = processMarketLines(data.player_goal_scorer_last, 'player_goal_scorer_last');
        firstLastGoalMarket.lines.push(...lastGoalLines);
      }

      // Only add the market if it has lines
      if (firstLastGoalMarket.lines.length > 0) {
        transformedData.markets.push(firstLastGoalMarket);
      }

      // Assists
      if (data.player_assists && Array.isArray(data.player_assists)) {
        transformedData.markets.push({
          marketKey: 'player_assists',
          marketName: 'Assists',
          category: 'assists',
          lines: processMarketLines(data.player_assists, 'player_assists')
        });
      }

      // Points (Goals + Assists)
      if (data.player_points && Array.isArray(data.player_points)) {
        transformedData.markets.push({
          marketKey: 'player_points',
          marketName: 'Points',
          category: 'points',
          lines: processMarketLines(data.player_points, 'player_points')
        });
      }

      // Shots on Goal
      if (data.player_shots_on_goal && Array.isArray(data.player_shots_on_goal)) {
        transformedData.markets.push({
          marketKey: 'player_shots_on_goal',
          marketName: 'Shots on Goal',
          category: 'shots',
          lines: processMarketLines(data.player_shots_on_goal, 'player_shots_on_goal')
        });
      }

      // Blocked Shots
      if (data.player_blocked_shots && Array.isArray(data.player_blocked_shots)) {
        transformedData.markets.push({
          marketKey: 'player_blocked_shots',
          marketName: 'Blocked Shots',
          category: 'blocked',
          lines: processMarketLines(data.player_blocked_shots, 'player_blocked_shots')
        });
      }

      // Saves (for goalies)
      if (data.player_saves && Array.isArray(data.player_saves)) {
        transformedData.markets.push({
          marketKey: 'player_saves',
          marketName: 'Saves',
          category: 'saves',
          lines: processMarketLines(data.player_saves, 'player_saves')
        });
      }

      setPlayerData(transformedData);
    } catch (err) {
      console.error('Error fetching NHL player data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processMarketLines = (marketLines, marketKey) => {
    if (!marketLines || !Array.isArray(marketLines)) return [];

    // New flat structure - just transform to modal format
    return marketLines.map(line => {
      // Convert flat structure to books object
      const books = {};

      // Extract all book data from the line
      Object.keys(line).forEach(key => {
        if (key.startsWith(`${marketKey}_`) && !key.includes('bestBook') && !key.includes('nextBook')) {
          const suffix = key.replace(`${marketKey}_`, '');
          const parts = suffix.split('_');

          if (parts.length >= 2) {
            const bookName = parts.slice(0, -1).join('_');
            const field = parts[parts.length - 1];

            if (field === 'over' || field === 'under' || field === 'point' || field === 'isAlternate' || field === 'yes' || field === 'no' || field === 'isAvailable') {
              if (!books[bookName]) {
                books[bookName] = {};
              }
              books[bookName][field] = line[key];
            }
            // Also capture isAvailable with _over or _under suffix
            if (field === 'over' || field === 'under') {
              const availKey = `${marketKey}_${bookName}_${field}_isAvailable`;
              if (line[availKey] !== undefined) {
                books[bookName][`${field}_isAvailable`] = line[availKey];
              }
            }
          } else if (parts.length === 1) {
            // For binary markets, odds are stored directly (e.g., player_goal_scorer_first_ballybet: 1700)
            const bookName = parts[0];
            if (typeof line[key] === 'number') {
              if (!books[bookName]) {
                books[bookName] = {};
              }
              // Store as 'yes' for binary markets
              books[bookName]['yes'] = line[key];
            }
          }
        }
      });

      // Determine label based on market type
      let label;
      if (line.point === 'yes' || line.point === 'no') {
        // For goal scorer markets, use specific labels
        if (marketKey === 'player_goal_scorer_first') {
          label = '1st';
        } else if (marketKey === 'player_goal_scorer_last') {
          label = 'Last';
        } else {
          label = 'Yes';
        }
      } else {
        label = `${line.point}`;
      }

      return {
        point: line.point === 'yes' ? null : line.point,
        label: label,
        books: books,
        bestBook: line[`${marketKey}_bestBook`],
        bestBookPrice: line[`${marketKey}_bestBookPrice`],
        bestBookDiff: line[`${marketKey}_bestBookDiff`],
        isSpecial: line.point === 'yes' || line.point === 'no',
        originalMarketKey: marketKey // Store original market key for chart fetching
      };
    }).sort((a, b) => {
      // Sort special markets (yes/no) to end
      if (a.isSpecial && !b.isSpecial) return 1;
      if (!a.isSpecial && b.isSpecial) return -1;
      return (a.point || 0) - (b.point || 0);
    });
  };

  // Prepare chart data
  const prepareChartData = () => {
    if (!oddsHistoryData || !oddsHistoryData.oddsHistory || oddsHistoryData.oddsHistory.length === 0) {
      return null;
    }

    // Filter by market key and point value
    // For special markets (1st goal, last goal), selectedTier will be null
    // For over/under markets, filter by the selected point value
    // Use tolerance for floating point comparison
    const filteredHistory = oddsHistoryData.oddsHistory.filter(entry => {
      // First, filter by market key (API uses "marketType" field)
      if (entry.marketType !== chartMarket) return false;

      // Then filter by point value if applicable
      if (selectedTier !== null) {
        const pointMatch = Math.abs(entry.point - selectedTier) < 0.01;
        return pointMatch;
      }

      return true;
    });

    if (filteredHistory.length === 0) {
      return null;
    }

    // Get all unique bookmakers from the filtered data
    const allBookmakers = new Set();
    filteredHistory.forEach(entry => {
      if (entry.books && typeof entry.books === 'object') {
        Object.keys(entry.books).forEach(book => {
          if (entry.books[book]) {
            if (entry.books[book].over !== undefined ||
                entry.books[book].yes !== undefined ||
                typeof entry.books[book] === 'number') {
              allBookmakers.add(book);
            }
          }
        });
      }
    });

    // Include every book; Settings → Graphs only sets the initial hidden flag below.
    const bookmakersArray = Array.from(allBookmakers);

    if (bookmakersArray.length === 0) return null;

    // Group entries by timestamp to handle multiple markets (main + alt) at same time
    // Normalize timestamps to nearest 5 minutes to combine entries that are close in time
    const groupedByTimestamp = {};
    filteredHistory.forEach(entry => {
      if (!entry.timestamp) return;
      const date = new Date(entry.timestamp);
      // Round to nearest 5 minutes
      const minutes = date.getMinutes();
      const roundedMinutes = Math.round(minutes / 5) * 5;
      date.setMinutes(roundedMinutes);
      date.setSeconds(0);
      date.setMilliseconds(0);
      const normalizedTimestamp = date.toISOString();

      if (!groupedByTimestamp[normalizedTimestamp]) {
        groupedByTimestamp[normalizedTimestamp] = [];
      }
      groupedByTimestamp[normalizedTimestamp].push(entry);
    });

    const timestamps = Object.keys(groupedByTimestamp).sort((a, b) => new Date(a) - new Date(b));

    if (timestamps.length === 0) return null;

    // Prepare labels (timestamps) - use unique timestamps
    const labels = timestamps.map(timestamp => {
      try {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (e) {
        return 'Invalid Date';
      }
    });

    // Color palette for bookmakers
    const bookmakerColors = {
      fanduel: '#1A3D6C',
      draftkings: '#5DD335',
      betmgm: '#FFCC00',
      fanatics: '#002F6C',
      espnbet: '#FF0033',
      betrivers: '#EC1C24',
      ballybet: '#000000',
      bovada: '#CC0000',
      caesars: '#000000',
      hardrockbet: '#D4145A'
    };

    // Prepare datasets for each bookmaker
    const datasetsWithLatestValues = bookmakersArray
      .map(bookmaker => {
        const data = timestamps.map(timestamp => {
          const entries = groupedByTimestamp[timestamp];
          const values = [];

          entries.forEach(entry => {
            if (entry.books && entry.books[bookmaker]) {
              let value;

              // For special markets (1st goal, last goal), use 'yes' or direct value
              if (selectedTier === null) {
                if (entry.books[bookmaker].yes !== undefined) {
                  value = parseFloat(entry.books[bookmaker].yes);
                } else if (entry.books[bookmaker].over !== undefined) {
                  value = parseFloat(entry.books[bookmaker].over);
                } else if (typeof entry.books[bookmaker] === 'number') {
                  value = parseFloat(entry.books[bookmaker]);
                }
              } else {
                // For over/under markets, use chartIsOver to determine which odds to show
                if (chartIsOver) {
                  // Check for 'yes' first (used for 0.5 goals), then 'over', then direct number
                  if (entry.books[bookmaker].yes !== undefined) {
                    value = parseFloat(entry.books[bookmaker].yes);
                  } else if (entry.books[bookmaker].over !== undefined) {
                    value = parseFloat(entry.books[bookmaker].over);
                  } else if (typeof entry.books[bookmaker] === 'number') {
                    value = parseFloat(entry.books[bookmaker]);
                  }
                } else {
                  // Show under odds
                  if (entry.books[bookmaker].under !== undefined) {
                    value = parseFloat(entry.books[bookmaker].under);
                  }
                }
              }

              if (value !== undefined && !isNaN(value)) {
                values.push(value);
              }
            }
          });

          // If multiple values exist (main + alt), use the best (highest) one
          if (values.length > 0) {
            return Math.max(...values);
          }
          return null;
        });

        const hasData = data.some(value => value !== null);
        if (!hasData) return null;

        // Get latest value for sorting
        let latestValue = null;
        for (let i = data.length - 1; i >= 0; i--) {
          if (data[i] !== null) {
            latestValue = data[i];
            break;
          }
        }

        // Calculate point radius
        const pointRadius = data.map((value, index) => {
          if (value === null) return 0;
          if (index === 0) return 3;
          if (index === data.length - 1) return 3;

          let prevValue = null;
          for (let i = index - 1; i >= 0; i--) {
            if (data[i] !== null) {
              prevValue = data[i];
              break;
            }
          }

          return (prevValue !== null && value !== prevValue) ? 3 : 0;
        });

        // Apply forward alias for display (e.g., williamhill_us -> caesars)
        const displayName = BOOKMAKER_ALIASES[bookmaker] || bookmaker;

        return {
          label: displayName.charAt(0).toUpperCase() + displayName.slice(1),
          data: data,
          borderColor: bookmakerColors[bookmaker] || '#999999',
          backgroundColor: bookmakerColors[bookmaker] || '#999999',
          borderWidth: 2,
          pointRadius: pointRadius,
          pointHoverRadius: 5,
          pointBackgroundColor: bookmakerColors[bookmaker] || '#999999',
          hidden: isBookEnabledForGraphs ? !isBookEnabledForGraphs(bookmaker) : false,
          latestValue: latestValue,
          spanGaps: true,
          tension: 0.1
        };
      })
      .filter(dataset => dataset !== null);

    // Sort datasets by latest value (descending)
    datasetsWithLatestValues.sort((a, b) => {
      if (a.latestValue === null && b.latestValue === null) return 0;
      if (a.latestValue === null) return 1;
      if (b.latestValue === null) return -1;
      return b.latestValue - a.latestValue;
    });

    return {
      labels,
      datasets: datasetsWithLatestValues
    };
  };

  const chartData = useMemo(() => {
    if (!show) return null;
    return prepareChartData();
  }, [show, oddsHistoryData, selectedTier, showChart, chartIsOver]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    backgroundColor: 'white',
    layout: {
      padding: {
        bottom: 20
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          fontSize: 12
        }
      },
      title: {
        display: true,
        text: `Odds Movement - ${oddsHistoryData?.marketSearch || chartMarket}${selectedTier !== null ? ` (${chartIsOver ? 'Over' : 'Under'} ${selectedTier})` : ''}`,
        font: {
          size: 16
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            if (value === null) return null;
            return `${context.dataset.label}: ${value > 0 ? '+' : ''}${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Odds'
        },
        ticks: {
          callback: function(value) {
            return value > 0 ? `+${value}` : value;
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  // Helper function to get tier color styling
  const getTierStyle = (odds, lineBooks) => {
    if (!odds || !lineBooks) return {};

    // Handle both over/under markets and special markets (yes/no)
    const allOdds = getScopedBooks(lineBooks)
      .map(b => b.over || b.yes)
      .filter(o => o !== null && o !== undefined);

    if (allOdds.length < 2) return {};

    const bestOdds = Math.max(...allOdds);
    const secondBestOdds = Math.max(...allOdds.filter(o => o !== bestOdds));

    // Handle cross-zero comparison (positive best odds vs negative second best)
    let diff;
    if (bestOdds > 0 && secondBestOdds < 0) {
      const spreadPositive = bestOdds - 100;
      const spreadNegative = secondBestOdds - (-100);
      const totalSpread = spreadPositive - spreadNegative;
      diff = Math.abs(totalSpread / secondBestOdds);
    } else {
      diff = (bestOdds - secondBestOdds) / Math.abs(secondBestOdds);
    }

    const bestOddsCount = allOdds.filter(o => o === bestOdds).length;
    const isBest = odds === bestOdds && bestOddsCount === 1;

    let style = {
      cursor: 'pointer',
      fontWeight: isBest ? 'bold' : 'normal'
    };

    if (isBest && allOdds.length > 1) {
      if (diff >= 0.25) {
        // Tier 1: Green with yellow text
        Object.assign(style, {
          backgroundColor: 'green',
          color: 'yellow',
          borderRadius: '3px'
        });
      } else if (diff >= 0.15) {
        // Tier 2: Amber with navy blue text
        Object.assign(style, {
          backgroundColor: '#F59E0B',
          color: '#1E40AF',
          borderRadius: '3px'
        });
      } else if (diff >= 0.05) {
        // Tier 3: Blue
        Object.assign(style, {
          backgroundColor: '#3B82F6',
          color: 'white',
          borderRadius: '3px'
        });
      } else {
        // Best odds but below tier threshold - use amber text
        Object.assign(style, {
          color: '#F59E0B'
        });
      }
    }

    return style;
  };

  // Desktop: Books in rows, point values in columns
  const renderDesktopTable = (market) => {
    if (!market || !market.lines || market.lines.length === 0) {
      return <p className="text-muted">No odds available for this market.</p>;
    }

    // Separate regular over/under lines from special markets (1st goal, last goal)
    const overUnderLines = market.lines.filter(line => !line.isSpecial);
    const specialLines = market.lines.filter(line => line.isSpecial);

    return (
      <div className="table-responsive">
        <Table bordered hover size="sm">
          <thead className="table-light">
            <tr>
              <th style={{ width: '120px', fontSize: '0.95rem' }}></th>
              {/* Over/Under columns */}
              {overUnderLines.map((line, idx) => (
                <th
                  key={`header-${idx}`}
                  style={{
                    textAlign: 'center',
                    padding: '8px',
                    fontSize: '0.95rem',
                    color: '#007bff',
                    cursor: 'pointer'
                  }}
                  onClick={() => fetchOddsHistory(market.marketKey, line.point, true)}
                >
                  {line.label}
                </th>
              ))}
              {/* Special markets columns */}
              {specialLines.map((line, idx) => (
                <th
                  key={`special-${idx}`}
                  style={{
                    textAlign: 'center',
                    padding: '8px',
                    fontSize: '0.95rem',
                    color: '#007bff',
                    cursor: 'pointer'
                  }}
                  onClick={() => fetchOddsHistory(line.originalMarketKey || market.marketKey, null, true)}
                >
                  {line.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookmakers.map(book => {
              const dataKey = getBookmakerDataKey(book);
              return (
              <tr key={book}>
                <td style={{ padding: '8px', textAlign: 'center', fontSize: '0.9rem' }}>
                  <img
                    src={getBookLogoUrl(book)}
                    alt={book}
                    style={{ height: '20px', maxWidth: '80px' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </td>
                {/* Over cells only (matching NFL pattern) */}
                {overUnderLines.map((line, idx) => {
                  const overOdds = line.books[dataKey]?.over;
                  const cellStyle = getTierStyle(overOdds, line.books);

                  return (
                    <td
                      key={`${book}-${idx}`}
                      style={{
                        textAlign: 'center',
                        padding: '8px',
                        fontSize: '0.9rem',
                        cursor: overOdds ? 'pointer' : 'default',
                        fontWeight: 'bold',
                        ...cellStyle,
                        opacity: line.books[dataKey]?.over_isAvailable === false ? 0.25 : undefined
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (overOdds) {
                          handleOddsClick(e, overOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books);
                        }
                        if (overOdds && onOddsClick) {
                          onOddsClick(gameId, playerName, 'over', market.marketKey, book, overOdds);
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile && overOdds) {
                          handleOddsClick(e, overOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile) {
                          setTooltipData(null);
                        }
                      }}
                    >
                      {overOdds ? (overOdds > 0 ? `+${overOdds}` : overOdds) : '-'}
                    </td>
                  );
                })}
                {/* Special market cells (1st goal, last goal) */}
                {specialLines.map((line, idx) => {
                  const odds = line.books[dataKey]?.yes || line.books[dataKey]?.over;
                  const specialCellStyle = getTierStyle(odds, line.books);
                  const isAvailable = line.books[dataKey]?.yes_isAvailable !== false && line.books[dataKey]?.over_isAvailable !== false;

                  return (
                    <td
                      key={`special-${book}-${idx}`}
                      style={{
                        textAlign: 'center',
                        padding: '8px',
                        fontSize: '0.9rem',
                        cursor: odds ? 'pointer' : 'default',
                        fontWeight: 'bold',
                        ...specialCellStyle,
                        opacity: !isAvailable ? 0.25 : undefined
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (odds) {
                          handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books);
                        }
                        if (odds && onOddsClick) {
                          onOddsClick(gameId, playerName, 'yes', market.marketKey, book, odds);
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile && odds) {
                          handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile) {
                          setTooltipData(null);
                        }
                      }}
                    >
                      {odds ? (odds > 0 ? `+${odds}` : odds) : '-'}
                    </td>
                  );
                })}
              </tr>
            );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  // Mobile: Same as desktop - Books in rows, point values in columns
  const renderMobileTable = (market) => {
    if (!market || !market.lines || market.lines.length === 0) {
      return <p className="text-muted">No odds available for this market.</p>;
    }

    // Separate regular over/under lines from special markets (1st goal, last goal)
    const overUnderLines = market.lines.filter(line => !line.isSpecial);
    const specialLines = market.lines.filter(line => line.isSpecial);

    return (
      <div className="table-responsive">
        <Table bordered hover size="sm">
          <thead className="table-light">
            <tr>
              <th style={{ width: '80px', fontSize: '0.95rem' }}></th>
              {/* Over/Under columns */}
              {overUnderLines.map((line, idx) => (
                <th
                  key={`header-${idx}`}
                  style={{
                    textAlign: 'center',
                    padding: '4px',
                    fontSize: '0.95rem',
                    color: '#007bff',
                    cursor: 'pointer'
                  }}
                  onClick={() => fetchOddsHistory(market.marketKey, line.point, true)}
                >
                  {line.label}
                </th>
              ))}
              {/* Special markets columns */}
              {specialLines.map((line, idx) => (
                <th
                  key={`special-${idx}`}
                  style={{
                    textAlign: 'center',
                    padding: '4px',
                    fontSize: '0.95rem',
                    color: '#007bff',
                    cursor: 'pointer'
                  }}
                  onClick={() => fetchOddsHistory(line.originalMarketKey || market.marketKey, null, true)}
                >
                  {line.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookmakers.map(book => {
              const dataKey = getBookmakerDataKey(book);
              return (
              <tr key={book}>
                <td style={{ padding: '4px', fontSize: '0.9rem', textAlign: 'center' }}>
                  <img
                    src={getBookLogoUrl(book)}
                    alt={book}
                    style={{ height: '16px', maxWidth: '60px' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </td>
                {/* Over/Under cells */}
                {overUnderLines.map((line, idx) => {
                  const overOdds = line.books[dataKey]?.over;
                  const cellStyle = getTierStyle(overOdds, line.books);

                  return (
                    <td
                      key={`${book}-${idx}`}
                      style={{
                        textAlign: 'center',
                        padding: '4px',
                        fontSize: '0.9rem',
                        cursor: overOdds ? 'pointer' : 'default',
                        fontWeight: 'bold',
                        ...cellStyle,
                        opacity: line.books[dataKey]?.over_isAvailable === false ? 0.25 : undefined
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (overOdds) {
                          const betMeta = {
                            gameId, playerId, playerName,
                            marketKey: market.marketKey,
                            book, odds: overOdds,
                            awayTeam, homeTeam, playerTeam, playerPosition,
                            line: line.point, side: 'over',
                          };
                          handleOddsClick(e, overOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                          if (!isMobile && onOddsClick) {
                            onOddsClick(gameId, playerId, playerName, market.marketKey, book, overOdds, awayTeam, homeTeam, playerTeam, playerPosition, line.point, 'over');
                          }
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile && overOdds) {
                          handleOddsClick(e, overOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile) {
                          setTooltipData(null);
                        }
                      }}
                    >
                      {overOdds ? (overOdds > 0 ? `+${overOdds}` : overOdds) : '-'}
                    </td>
                  );
                })}
                {/* Special market cells (1st goal, last goal) */}
                {specialLines.map((line, idx) => {
                  const odds = line.books[dataKey]?.yes || line.books[dataKey]?.over;
                  const specialCellStyle = getTierStyle(odds, line.books);
                  const isAvailable = line.books[dataKey]?.yes_isAvailable !== false && line.books[dataKey]?.over_isAvailable !== false;

                  return (
                    <td
                      key={`special-${book}-${idx}`}
                      style={{
                        textAlign: 'center',
                        padding: '4px',
                        fontSize: '0.9rem',
                        cursor: odds ? 'pointer' : 'default',
                        fontWeight: 'bold',
                        ...specialCellStyle,
                        opacity: !isAvailable ? 0.25 : undefined
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (odds) {
                          const betMeta = {
                            gameId, playerId, playerName,
                            marketKey: line.originalMarketKey || market.marketKey,
                            book, odds,
                            awayTeam, homeTeam, playerTeam, playerPosition,
                            line: line.label, side: 'yes',
                          };
                          handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books, betMeta);
                          if (!isMobile && onOddsClick) {
                            onOddsClick(gameId, playerId, playerName, line.originalMarketKey || market.marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line.label, 'yes');
                          }
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile && odds) {
                          handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile) {
                          setTooltipData(null);
                        }
                      }}
                    >
                      {odds ? (odds > 0 ? `+${odds}` : odds) : '-'}
                    </td>
                  );
                })}
              </tr>
            );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  const currentMarket = playerData?.markets.find(m => m.category === selectedMarketType);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      fullscreen="sm-down"
      centered
      scrollable
      className="nhl-player-modal"
      backdropClassName="player-modal-backdrop"
    >
      {isMobile && <ModalBrandingBar />}
      <Modal.Header closeButton style={{ borderBottom: '2px solid #dee2e6' }}>
        <Modal.Title className="w-100">
          {playerData?.player && (
            <div className="d-flex align-items-center gap-3">
              <img
                src={playerData.player.photo}
                alt={playerName}
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #dee2e6'
                }}
                onError={(e) => {
                  if (e.target.src !== 'https://via.placeholder.com/60x60?text=No+Photo') {
                    e.target.src = 'https://via.placeholder.com/60x60?text=No+Photo';
                  }
                }}
              />
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="mb-0">{playerName}</h5>
                  {playerData.player.jerseyNumber && (
                    <Badge bg="secondary">#{playerData.player.jerseyNumber}</Badge>
                  )}
                </div>
                <div className="text-muted small d-flex align-items-center gap-2">
                  <img
                    src={playerData.player.teamLogo || getTeamLogoUrl(playerTeam)}
                    alt={playerTeam}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span>{playerTeam} • {playerPosition}</span>
                  <AddToScratchpadLink
                    playerData={{ gameId, playerId, playerName, playerTeam }}
                    onToggleScratchpad={onToggleScratchpad}
                    isInScratchpad={isInScratchpad}
                  />
                </div>
              </div>
            </div>
          )}
        </Modal.Title>
      </Modal.Header>

      {/* No inline height cap — the Modal `scrollable` prop + the shared
          .*-player-modal CSS in App.css puts the body in a flex column that
          fills the space under a pinned Header, so the player info stays
          fixed as the market tables scroll. */}
      <Modal.Body>
        {loading && (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <Spinner animation="border" />
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            <h5>Error loading player data</h5>
            <p>{error}</p>
            <button className="btn btn-primary btn-sm" onClick={fetchPlayerData}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && playerData && !showChart && (
          <>
            {/* Market tabs - always show all markets */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
              {['goals', 'assists', 'points', 'shots', 'goal_scorers'].map(category => {
                const market = playerData.markets.find(m => m.category === category);
                const hasData = !!market;
                const marketName = category === 'goals' ? 'Goals'
                  : category === 'assists' ? 'Assists'
                  : category === 'points' ? 'Points'
                  : category === 'shots' ? 'Shots on Goal'
                  : category === 'goal_scorers' ? '1st/Last'
                  : category;

                return (
                  <button
                    key={category}
                    className={`btn btn-sm ${selectedMarketType === category ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => hasData && setSelectedMarketType(category)}
                    disabled={!hasData}
                    style={{
                      opacity: hasData ? 1 : 0.5,
                      cursor: hasData ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {marketName}
                  </button>
                );
              })}
            </div>

            {/* Market table */}
            {currentMarket ? (
              isMobile ? renderMobileTable(currentMarket) : renderDesktopTable(currentMarket)
            ) : (
              <p className="text-muted">Select a market to view odds.</p>
            )}

            {/* Per-game stats chart — shared with the desktop panel so
                Current/Last toggle, bar sizing, y-axis behavior, empty state,
                and grid all stay identical across surfaces. */}
            {currentMarket && (
              <div style={{ marginTop: '20px' }}>
                <PlayerStatsChart
                  playerId={playerId?.replace('nhl_', '')}
                  sport="nhl"
                  marketType={selectedMarketType}
                  playerTeam={playerTeam}
                  awayTeam={awayTeam}
                  homeTeam={homeTeam}
                  lineValue={getPrimaryLinePoint(currentMarket)}
                />
              </div>
            )}

            {/* Helper text for chart functionality */}
            <div style={{
              textAlign: 'center',
              fontSize: '0.85rem',
              color: '#6c757d',
              marginTop: '16px',
              padding: '8px'
            }}>
              📊 Click a point value to see odds movement
            </div>
          </>
        )}

        {!loading && !error && showChart && (
          <>
            {/* Back Button */}
            <div className="mb-3">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleBackToTable}
              >
                ← Back to Odds Table
              </Button>
            </div>

            {loadingChart && (
              <div className="d-flex justify-content-center align-items-center" style={{ height: isMobile ? '70vh' : '400px' }}>
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">Loading chart...</span>
                </Spinner>
              </div>
            )}

            {!loadingChart && chartData && chartData.datasets && chartData.datasets.length > 0 && chartData.labels && chartData.labels.length > 0 ? (
              <div style={{
                height: isMobile ? '70vh' : '400px',
                position: 'relative',
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                <Line
                  key={`chart-${gameId}-${playerId}-${chartMarket}-${chartPoint}`}
                  data={chartData}
                  options={{
                    ...chartOptions,
                    animation: {
                      duration: 0
                    },
                    plugins: {
                      ...chartOptions.plugins,
                      title: {
                        ...chartOptions.plugins.title,
                        font: {
                          size: isMobile ? 14 : 16
                        }
                      },
                      legend: {
                        position: 'bottom',
                        labels: {
                          boxWidth: isMobile ? 10 : 12,
                          fontSize: isMobile ? 11 : 12
                        }
                      }
                    }
                  }}
                  plugins={[]}
                />
              </div>
            ) : !loadingChart && (
              <div className="alert alert-warning" role="alert">
                No odds movement data available for this market.
              </div>
            )}
          </>
        )}

        {/* Desktop tooltip (hover popover) */}
        {tooltipData && !isMobile && (
          <div
            style={{
              position: 'fixed',
              left: `${tooltipData.x + 10}px`,
              top: `${tooltipData.y + 10}px`,
              backgroundColor: '#1f2937',
              color: 'white',
              padding: '12px 16px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              zIndex: 9999,
              fontSize: '0.85rem',
              minWidth: '200px',
              pointerEvents: 'none'
            }}
          >
            <div style={{ marginBottom: '8px', fontWeight: 'bold', borderBottom: '1px solid #4b5563', paddingBottom: '6px' }}>
              {tooltipData.book.charAt(0).toUpperCase() + tooltipData.book.slice(1)} ({tooltipData.point})
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Odds:</span>
              <span style={{ fontWeight: 'bold' }}>{tooltipData.odds > 0 ? `+${tooltipData.odds}` : tooltipData.odds}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Implied Prob:</span>
              <span style={{ fontWeight: 'bold', color: '#60a5fa' }}>{tooltipData.impliedProb}%</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Edge:</span>
              <span style={{ fontWeight: 'bold', color: parseFloat(tooltipData.edge) > 0 ? '#34d399' : '#f87171' }}>
                {parseFloat(tooltipData.edge) > 0 ? '+' : ''}{tooltipData.edge}%
              </span>
            </div>
          </div>
        )}

        {/* Mobile bottom sheet with Add/Remove to Betslip — matches NFL. */}
        {tooltipData && isMobile && (
          <div
            role="dialog"
            aria-label="Odds detail"
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: '#1f2937',
              color: 'white',
              padding: '14px 16px calc(16px + env(safe-area-inset-bottom))',
              boxShadow: '0 -6px 20px rgba(0,0,0,0.5)',
              borderTop: '1px solid #374151',
              borderTopLeftRadius: '14px',
              borderTopRightRadius: '14px',
              zIndex: 1210,
              fontSize: '0.9rem',
              animation: 'toastFadeUp 0.2s ease-out',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                {tooltipData.book.charAt(0).toUpperCase() + tooltipData.book.slice(1)} ({tooltipData.point})
              </span>
              <button
                type="button"
                onClick={() => setTooltipData(null)}
                aria-label="Close"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#d1d5db',
                  fontSize: '1.4rem',
                  lineHeight: 1,
                  padding: '4px 10px',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              <div>
                <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Odds</div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{tooltipData.odds > 0 ? `+${tooltipData.odds}` : tooltipData.odds}</div>
              </div>
              <div>
                <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Implied</div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#60a5fa' }}>{tooltipData.impliedProb}%</div>
              </div>
              <div>
                <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Edge</div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: parseFloat(tooltipData.edge) > 0 ? '#34d399' : '#f87171' }}>
                  {parseFloat(tooltipData.edge) > 0 ? '+' : ''}{tooltipData.edge}%
                </div>
              </div>
            </div>

            {/* Add / Remove button — match this bet against betslipItems by
                gameId + playerId + market + line + side. Normalize playerId
                and line because the main odds screen and the modal can
                carry them in different shapes. */}
            {(() => {
              const m = tooltipData.meta;
              if (!m) return null;
              const stripSportPrefix = (id) => String(id ?? '').replace(/^[a-z]+_/i, '');
              const normLine = (v) => {
                if (v == null || v === '') return v;
                const n = parseFloat(v);
                return Number.isFinite(n) ? n : v;
              };
              const targetPlayerId = stripSportPrefix(m.playerId);
              const targetLine = normLine(m.line);
              const existing = betslipItems.find(bet =>
                bet.gameId === m.gameId &&
                stripSportPrefix(bet.playerId) === targetPlayerId &&
                bet.market === m.marketKey &&
                normLine(bet.line) === targetLine &&
                bet.side === m.side
              );
              const btnBase = {
                width: '100%',
                marginTop: '12px',
                padding: '12px 14px',
                fontSize: '0.95rem',
                fontWeight: 700,
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                color: '#fff',
              };
              if (existing) {
                return (
                  <button
                    type="button"
                    style={{ ...btnBase, backgroundColor: '#dc2626' }}
                    onClick={() => {
                      if (onRemoveBet) onRemoveBet(existing.id);
                      setTooltipData(null);
                    }}
                  >
                    Remove from Betslip
                  </button>
                );
              }
              return (
                <button
                  type="button"
                  style={{ ...btnBase, backgroundColor: '#10b981' }}
                  onClick={() => {
                    if (onOddsClick) {
                      onOddsClick(
                        m.gameId, m.playerId, m.playerName, m.marketKey,
                        m.book, m.odds, m.awayTeam, m.homeTeam,
                        m.playerTeam, m.playerPosition, m.line, m.side
                      );
                    }
                    setTooltipData(null);
                  }}
                >
                  Add to Betslip
                </button>
              );
            })()}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default NHLPlayerModal;
